import {
  setPageInfo,
  setPageInfoWithCustom,
  setErrorPageInfo,
  addCustomInfo,
  addCustomPageInfo,
  pushErrorEvent,
  pushEventInfo,
  pushEventNameCategorySetting,
  pushStashEventInfo,
  pushEventNameAndCategory,
} from './gather';
import { ErrorContent } from '../constants/returns';
import CONSTANTS from './constants';
import { FULFILLMENTS } from '../constants';
import { createApplianceCancelPostOrder } from './utils';

const {
  PAGE_TYPE,
  PAGE_NAME,
  PRIMARY_CATEGORY,
  SUB_CATEGORY1,
  SUB_CATEGORY2,
  PAGE_LOAD_EVENT,
  TYPE,
  DISPLAY_TYPE,
  PAGE_SECTION,
  MESSAGE,
  ERROR_MESSAGE,
  ERROR_TYPE,
  CODE,
  CATEGORY,
  EVENT_NAME,
  EVENT_INFO,
  MY_ACCOUNT,
} = CONSTANTS;

const ORDER_DETAILS_PAGE_TYPE = 'maml:orders';

/**
 * @Order_Tracking: Order Tracking Page Load - Guest
 */
export const setOrderTrackingPageInfo = () => {
  setPageInfo(
    {
      [PAGE_TYPE]: ORDER_DETAILS_PAGE_TYPE,
      [PAGE_NAME]: 'check your order status',
    },
    {
      [PRIMARY_CATEGORY]: 'my account',
      [SUB_CATEGORY1]: 'check your order status',
      [SUB_CATEGORY2]: 'check your order status',
    }
  );
};

/**
 * @Order_Details: Online Order Page Load - Guest
 */
export const setOrderDetailsPageInfo = (fulfillments: string = '') => {
  setPageInfo(
    {
      [PAGE_TYPE]: ORDER_DETAILS_PAGE_TYPE,
      [PAGE_NAME]: 'track your order>online orders',
      [PAGE_LOAD_EVENT]: 'order view',
    },
    {
      [PRIMARY_CATEGORY]: 'my account',
      [SUB_CATEGORY1]: 'my account>online orders',
      [SUB_CATEGORY2]: 'my account>online orders',
    },
    fulfillments && { myAccount: { settings: fulfillments } }
  );
};

export const setOrderHistoryPageError = () => {
  pushErrorEvent({
    [TYPE]: 'system',
    [DISPLAY_TYPE]: 'inline',
    [PAGE_SECTION]: 'order summary',
    [MESSAGE]: `we're sorry! a temporary connection error prevented your request from going through. please try again.`,
  });
};

export const setOrderHistoryOrderDetailsClickError = () => {
  pushErrorEvent({
    [TYPE]: 'error',
    [DISPLAY_TYPE]: 'inline',
    [PAGE_SECTION]: 'order details',
    [MESSAGE]: `we're sorry! a temporary connection error prevented your request from going through. please try again.`,
  });
};

/**
 * @Cancel_Items: Initial Page Load
 */
export const setCancellationPageInfo = () => {
  setPageInfo(
    {
      [PAGE_TYPE]: ORDER_DETAILS_PAGE_TYPE,
      [PAGE_NAME]: 'my account>online orders>cancel items',
    },
    {
      [PRIMARY_CATEGORY]: 'my account',
      [SUB_CATEGORY1]: 'my account>online orders',
      [SUB_CATEGORY2]: 'my account>online orders>cancel items',
    }
  );
};

/**
 * @Cancel_Items: Success Page Load
 * @param {*} postOrder
 */
export const setCancellationSuccessPageInfo = (postOrder: Object) => {
  addCustomInfo('postOrder', postOrder);
  setPageInfo(
    {
      [PAGE_NAME]: 'my account>online orders>cancel items>cancel confirmation',
      [PAGE_TYPE]: ORDER_DETAILS_PAGE_TYPE,
      [PAGE_LOAD_EVENT]: 'cancel success',
    },
    {
      [PRIMARY_CATEGORY]: 'my account',
      [SUB_CATEGORY1]: 'my account>online orders',
      [SUB_CATEGORY2]: 'my account>online orders>cancel items',
    }
  );
};

/**
 * @Appliance_Cancel: Initial Page Load
 */
export const setApplianceCancelPageInfo = () => {
  setPageInfo(
    {
      [PAGE_TYPE]: ORDER_DETAILS_PAGE_TYPE,
      [PAGE_NAME]: 'my account>online orders>cancel appliance',
    },
    {
      [PRIMARY_CATEGORY]: 'my account',
      [SUB_CATEGORY1]: 'my account>online orders',
      [SUB_CATEGORY2]: 'my account>online orders>cancel appliance',
    }
  );
};

/**
 * @Appliance_Cancel: Success Page Load
 * @param {*} postOrder
 */
export const setApplianceCancelSuccess = (
  eligibleItems: Object,
  mappedLineItems: Object,
  orderDetails: Object
) => {
  addCustomInfo(
    'postOrder',
    createApplianceCancelPostOrder(eligibleItems, mappedLineItems, orderDetails)
  );
  setPageInfo(
    {
      [PAGE_NAME]:
        'my account>online orders>appliance cancel>cancel confirmation',
      [PAGE_TYPE]: ORDER_DETAILS_PAGE_TYPE,
      [PAGE_LOAD_EVENT]: 'cancel success',
    },
    {
      [PRIMARY_CATEGORY]: 'my account',
      [SUB_CATEGORY1]: 'my account>online orders',
      [SUB_CATEGORY2]: 'my account>online orders>appliance cancel',
    }
  );
};

/**
 * Generic Method for Setting Error Event
 * @param {*} message required
 * @param {*} pageSection required
 * @param {*} code required
 * @param {*} type optional
 * @param {*} displayType optional
 */
export const setErrorEvent = (
  message: string,
  pageSection: string,
  code: string,
  type?: string,
  displayType?: string
) => {
  const errorEvent = {
    type: type || 'error',
    displayType: displayType || 'inline',
    message: message ? message.toLowerCase() : '',
    pageSection,
    code,
  };
  pushErrorEvent(errorEvent);
};

/**
 * @Cancel_Items: Set Error Event
 * @param {*} message
 * @param {*} code
 */
export const setCancellationErrorPageInfo = (message: string, code: string) => {
  const pageSection = 'order cancellation';
  setErrorEvent(message, pageSection, code);
};

export const setApplianceCancelErrorEvent = (message: string, code: string) => {
  const pageSection = 'cancel appliance';
  setErrorEvent(message, pageSection, code);
};

const createFulfillmentInfo = (groupByFulfillment: Object) => {
  let orderString: string = 'order:';
  orderString += groupByFulfillment.SHIPTOHOME ? 'sth>y|' : 'sth>n|';
  orderString += groupByFulfillment.BUYONLINESHIPTOSTORE
    ? 'boss>y|'
    : 'boss>n|';
  orderString += groupByFulfillment.BUYONLINEPICKUPINSTORE
    ? 'bopis>y|'
    : 'bopis>n|';
  orderString += groupByFulfillment.BUYONLINEDELIVERFROMSTORE
    ? 'bodfs>y|'
    : 'bodfs>n|';
  orderString += groupByFulfillment.APPLIANCE ? 'appl>y|' : 'appl>n|';
  orderString += groupByFulfillment.MISC ? 'unknown>y' : 'unknown>n';
  return orderString;
};
/**
 * @Order_Details: Set Online Order Fulfillments - Guest
 * @param {*} groupByFulfillment
 */
export const setOrderDetailsFulfillment = (groupByFulfillment: Object) => {
  const settings = createFulfillmentInfo(groupByFulfillment);
  addCustomPageInfo('myAccount', { settings });
};

export const setOrderHistoryClicked = (groupByFulfillment: Object) => {
  pushEventInfo({
    [EVENT_INFO]: {
      [EVENT_NAME]: 'my account>online orders',
    },
    [CATEGORY]: {
      [PRIMARY_CATEGORY]: 'expansion',
    },
    [MY_ACCOUNT]: {
      settings: createFulfillmentInfo(groupByFulfillment),
    },
  });
};

export const setOrderHistoryPageInfoWithError = (errorCode: string = '') => {
  setErrorPageInfo([
    {
      [TYPE]: 'error',
      [DISPLAY_TYPE]: 'inline',
      [PAGE_SECTION]: 'order summary',
      [MESSAGE]: `we're sorry! a temporary connection error prevented your request from going through. please try again.`,
      [CODE]: errorCode,
    },
  ]);
};

export const setOrderHistoryPageInfo = (groupByFulfillment: Object) => {
  // Don't create fulfillment string when returning back to order history page
  const settings = groupByFulfillment
    ? { settings: createFulfillmentInfo(groupByFulfillment) }
    : {};
  setPageInfoWithCustom(
    {
      [PAGE_TYPE]: ORDER_DETAILS_PAGE_TYPE,
      [PAGE_NAME]: 'my account>online orders',
      [PAGE_LOAD_EVENT]: 'order view',
    },
    settings
  );
};

/**
 * @Order_Details: Set Status Details Event
 */
export const setStatusDetailsInfo = () => {
  const eventName = 'my account settings';
  const eventCategory = 'button submission';
  const eventSetting = 'orders: see status details';
  pushEventNameCategorySetting(eventName, eventCategory, eventSetting);
};

/**
 * @Order_Details: Set Shipment Tracking Event - STH & BOSS
 */
export const setShipmentTracking = (success: boolean, filter: string) => {
  const eventName = 'my account settings';
  const eventCategory = 'button submission';
  const eventType = success ? 'ic' : 'nic';
  const fulfillmentType = filter === 'sthTracking' ? 'sth' : 'boss';
  const eventSetting = `orders>${fulfillmentType}>${eventType}: tracking number`;
  pushEventNameCategorySetting(eventName, eventCategory, eventSetting);
};

/**
 * @Order_Details: Set View Receipt Event
 * @param {*} isInstore
 * Called with Instore is true for Instore Orders
 */
export const setReceiptClicked = (isInstore: boolean = false) => {
  const eventName = 'my account settings';
  const eventCategory = 'button submission';
  const eventSetting = isInstore
    ? 'orders: in-store view receipt'
    : 'orders: view receipt';
  pushEventNameCategorySetting(eventName, eventCategory, eventSetting);
};

/**
 * @Order_Details: Set Info Guides Event
 */
export const setInfoGuidesClicked = () => {
  const eventName = 'info and guides';
  const eventCategory = 'overlay';
  pushEventNameCategorySetting(eventName, eventCategory);
};

export const setBopisCancellationInfo = () => {
  pushEventInfo({
    [EVENT_INFO]: {
      [EVENT_NAME]: 'bopis cancellation',
    },
    [CATEGORY]: {
      [PRIMARY_CATEGORY]: 'overlay',
    },
  });
};

export const setBopisOrderCancelled = () => {
  pushEventInfo({
    [EVENT_INFO]: {
      [EVENT_NAME]: 'bopis cancellation success',
    },
    [CATEGORY]: {
      [PRIMARY_CATEGORY]: 'button submission',
    },
    [MY_ACCOUNT]: { orderCancel: 'bopis' },
  });
};

export const setBopisCancelError = () => {
  pushErrorEvent({
    [TYPE]: 'system',
    [DISPLAY_TYPE]: 'inline',
    [PAGE_SECTION]: 'order cancellation',
    [MESSAGE]: '',
  });
};

/**
 *  Called with different sharedSections based on the page and auth vs guest users
 * @param {*} itemId
 * @param {*} sharedSection : 'cancelled order' , 'authenticated order', 'guest order', 'in-store order'
 */
export const setAddToCartEventInfo = (
  itemId: string,
  sharedSection: string = 'guest order'
) => {
  const isSubscriptionChecked = sharedSection === 'subscription';
  pushStashEventInfo('cart add location', 'stash', {
    pageType: isSubscriptionChecked ? 'maml' :'orderdetails',
    sharedSection,
    section: isSubscriptionChecked? 'online orders':'buy again',
    component: 'single',
    target: 'atc',
    items: [
      {
        productID: itemId,
      },
    ],
  });
};
export const setReturnsPageInfo = (page: string, isError: boolean = false) => {
  const pages = {
    landing: () =>
      setPageInfo(
        {
          [PAGE_TYPE]: ORDER_DETAILS_PAGE_TYPE,
          [PAGE_NAME]: `my account>start a return${isError ? ' error' : ''}`,
        },
        {
          [PRIMARY_CATEGORY]: `my account`,
          [SUB_CATEGORY1]: `my account>start a return${
            isError ? ' error' : ''
          }`,
          [SUB_CATEGORY2]: `my account>start a return${
            isError ? ' error' : ''
          }`,
        }
      ),
    eligibility: () =>
      setPageInfo(
        {
          [PAGE_TYPE]: ORDER_DETAILS_PAGE_TYPE,
          [PAGE_NAME]: `my account>online return eligibility${
            isError ? ' error' : ''
          }`,
        },
        {
          [PRIMARY_CATEGORY]: `my account`,
          [SUB_CATEGORY1]: `my account>online return eligibility${
            isError ? ' error' : ''
          }`,
          [SUB_CATEGORY2]: `my account>online return eligibility${
            isError ? ' error' : ''
          }`,
        }
      ),
    returnsCreate: () =>
      setPageInfo(
        {
          [PAGE_TYPE]: ORDER_DETAILS_PAGE_TYPE,
          [PAGE_NAME]: `my account>started your return${
            isError ? ' error' : ''
          }`,
        },
        {
          [PRIMARY_CATEGORY]: `my account`,
          [SUB_CATEGORY1]: `my account>started your return${
            isError ? ' error' : ''
          }`,
          [SUB_CATEGORY2]: `my account>started your return${
            isError ? ' error' : ''
          }`,
        }
      ),
    returnsError: () =>
      setPageInfo(
        {
          [PAGE_TYPE]: ORDER_DETAILS_PAGE_TYPE,
          [PAGE_NAME]: `my account>error`,
        },
        {
          [PRIMARY_CATEGORY]: `my account`,
          [SUB_CATEGORY1]: `my account>error`,
          [SUB_CATEGORY2]: `my account>error`,
        }
      ),
  };

  pages[page]();
};

export const setReturnsAnalyticsErrors = (action: string) => {
  const errors = {
    general: { [MESSAGE]: ErrorContent.ERROR_GENERIC_ERROR, [CODE]: 'general' },
    ssr_start_err: {
      [MESSAGE]: ErrorContent.ERROR_BACK_END,
      [CODE]: 'ssr_start_err',
    },
    ssr_eligibility_err: {
      [MESSAGE]: ErrorContent.BANNER_TEXT.toLowerCase(),
      [CODE]: 'ssr_eligibility_err',
    },
    ssr_submission_err: {
      [MESSAGE]: '',
      [CODE]: 'ssr_submission_err',
    },
    ssr_giftcard: {
      [MESSAGE]: ErrorContent.ERROR_GIFT_CARD_MESSAGE,
      [CODE]: 'ssr_giftcard',
    },
    ssr_m_vendor: {
      [MESSAGE]: ErrorContent.ERROR_MUTLI_VENDOR_MESSAGE,
      [CODE]: 'ssr_m_vendor',
    },
    ssr_no_items: {
      [MESSAGE]: ErrorContent.NO_ITEMS_SELECTED,
      [CODE]: 'ssr_no_items',
    },
    ssr_dropdown_unselected: {
      [MESSAGE]: ErrorContent.NO_BOXES_SELECTED,
      [CODE]: 'ssr_dropdown_unselected',
    },
  };

  pushErrorEvent({
    [TYPE]: 'error',
    [DISPLAY_TYPE]: 'inline',
    [PAGE_SECTION]: 'ssr',
    [MESSAGE]: errors[action][MESSAGE],
    [CODE]: errors[action][CODE],
  });
};

export const setDestroyForCreditCount = (dfcCount: Number) => {
  pushErrorEvent({
    [TYPE]: 'error',
    [DISPLAY_TYPE]: 'inline',
    [PAGE_SECTION]: 'ssr',
    [MESSAGE]: dfcCount.toString(),
    [CODE]: 'ssr_other',
  });
};

// Appliance Analytics
export const setApplianceDeliveryRescheduleInfo = () => {
  pushEventNameAndCategory(
    'successful appliance reschedule',
    'button submission'
  );
};

export const setADRSuccessOverlayInfo = () => {
  pushEventNameAndCategory('successfully changed delivery date', 'overlay');
};

export const applianceDeliveryRescheduleError = () => {
  pushErrorEvent({
    pageSection: 'appliance delivery reschedule',
    displayType: 'inline',
    errorMessage:
      "we're sorry. unable to update your delivery date, please try again later.",
  });
};

export const setChangeDeliveryDateOverlayInfo = () => {
  pushEventNameAndCategory('reschedule appliance delivery request', 'overlay');
};

export const errorCalendarResponse = () => {
  pushErrorEvent({
    pageSection: 'appliance delivery calendar',
    displayType: 'inline',
    errorMessage:
      "we're sorry. there was a temporary technical issue. please try again shortly.",
  });
};

export const setTrackApplianceDelivery = (depotDirect: Object) => {
  const { deliveryTrackerUrl } = depotDirect;
  const deliveryTrackerType =
    deliveryTrackerUrl.indexOf('geappl') !== -1
      ? 'orders: appl tracker non tad'
      : 'orders: appl tracker tad';
  const eventName = 'my account settings';
  const eventCategory = 'button submission';
  const eventSetting = deliveryTrackerType;
  pushEventNameCategorySetting(eventName, eventCategory, eventSetting);
};

// End of Appliance Analytics

// BODFS & TYT
export const setTrackYourTruckClicked = () => {
  pushEventNameAndCategory('track your truck', 'overlay');
};

export const setTrackYourTruckError = () => {
  pushErrorEvent({
    [ERROR_TYPE]: 'system',
    [PAGE_SECTION]: 'track your truck overlay',
    [DISPLAY_TYPE]: 'inline',
    [ERROR_MESSAGE]: `we're sorry. there was a temporary technical issue. please try again shortly.`,
  });
};

export const setDeliveryLinkTracking = (tracking: string) => {
  const trackerType = tracking.includes("deliv")? "deliv": "roadie";
  pushEventInfo({
    [EVENT_INFO]: {
      [EVENT_NAME]: 'my account settings',
    },
    [CATEGORY]: {
      [PRIMARY_CATEGORY]: 'button submission',
    },
    [MY_ACCOUNT]: {
      settings: `${trackerType} order`,
    },
  });
};

/**
 * @ What You Need to Know Overlay
 * @param {string} fulfillmentType
 */
export const setWhatYouNeedToKnow = (fulfillmentType: string) => {
  const eventType =
    fulfillmentType === FULFILLMENTS.APPLIANCE
      ? 'appl'
      : fulfillmentType.toLowerCase();
  pushEventNameAndCategory(`what you need to know ${eventType}`, 'overlay');
};

export const setDocumentInfo = (text: string) => {
  pushEventNameCategorySetting(
    'my account settings',
    'button submission',
    `orders: ${text.toLowerCase()}`
  );
};

/**
 *  In-Store Analytics - Page Info
 */
export const setInstoreOrderPageInfo = () => {
  setPageInfo(
    {
      [PAGE_TYPE]: ORDER_DETAILS_PAGE_TYPE,
      [PAGE_NAME]: `my account>in-store orders`,
    },
    {
      [PRIMARY_CATEGORY]: `my account`,
      [SUB_CATEGORY1]: `my account>in-store orders`,
      [SUB_CATEGORY2]: `my account>in-store orders`,
    }
  );
};

export const setInstoreOrderDetailsPageInfo = (fulfillments: string = '') => {
  setPageInfo(
    {
      [PAGE_TYPE]: ORDER_DETAILS_PAGE_TYPE,
      [PAGE_NAME]: `my account>in-store orders>order details`,
      [PAGE_LOAD_EVENT]: `order view`,
    },
    {
      [PRIMARY_CATEGORY]: `my account`,
      [SUB_CATEGORY1]: `my account>in-store orders`,
      [SUB_CATEGORY2]: `my account>in-store orders>order details`,
    },
    fulfillments && { myAccount: { settings: fulfillments } }
  );
};

/**
 * More fulfillments to be added here - currently we only support Appliance, Dfs, and Misc
 * @param {*} groupByFulfillment
 */
const createInstoreFulfillmentInfo = (groupByFulfillment: Object) => {
  let orderString: string = 'in-store order:';
  orderString += groupByFulfillment.DFS ? 'dfs>y|' : 'dfs>n|';
  orderString += groupByFulfillment.APPLIANCE ? 'appl>y|' : 'appl>n|';
  orderString += groupByFulfillment.MISC ? 'unknown>y' : 'unknown>n';
  return orderString;
};

/**
 * @InStore_Orders: Set In-store Order Fulfillments
 * @param {*} groupByFulfillment
 */
export const setInStoreOrderDetailsFulfillment = (
  groupByFulfillment: Object
) => {
  const settings = createInstoreFulfillmentInfo(groupByFulfillment);
  addCustomPageInfo('myAccount', { settings });
};

/**
 * @Order_Details: Set View Receipt Event
 * @param {*} isInstore
 * Called with Instore is true for Instore Orders
 */
export const setCustomerServiceAgreementClicked = () => {
  const eventName = 'my account settings';
  const eventCategory = 'button submission';
  const eventSetting = 'orders: in-store customer service agreement';
  pushEventNameCategorySetting(eventName, eventCategory, eventSetting);
};

export const setPageInfoAndOrderFulfillment = (
  isInstore: boolean,
  groupByFulfillment: Object
) => {
  if (isInstore) {
    setInstoreOrderDetailsPageInfo(
      createInstoreFulfillmentInfo(groupByFulfillment)
    );
    // setInStoreOrderDetailsFulfillment(groupByFulfillment);
  } else {
    setOrderDetailsPageInfo(createFulfillmentInfo(groupByFulfillment));
    // setOrderDetailsFulfillment(groupByFulfillment);
  }
};

export const setEReceiptSummaryPageInfo = () => {
  setPageInfo(
    {
      [PAGE_TYPE]: ORDER_DETAILS_PAGE_TYPE,
      [PAGE_NAME]: 'my account>in-store e-receipts',
    },
    {
      [PRIMARY_CATEGORY]: 'my account',
      [SUB_CATEGORY1]: 'my account>in-store e-receipts',
      [SUB_CATEGORY2]: 'my account>in-store e-receipts',
    }
  );
};

export const setEReceiptDetailsPageInfo = () => {
  pushEventInfo({
    [EVENT_INFO]: {
      [EVENT_NAME]: 'my account>in-store e-receipts',
    },
    [CATEGORY]: {
      [PRIMARY_CATEGORY]: 'expansion',
    },
  });
};

export const setEReceiptSummaryError = () => {
  pushErrorEvent({
    [TYPE]: 'system',
    [DISPLAY_TYPE]: 'inline',
    [PAGE_SECTION]: 'in-store e-receipts',
    [MESSAGE]: `we're sorry! a temporary connection error prevented your request from going through. please try again.`,
  });
};

export const setEReceiptDetailError = () => {
  pushErrorEvent({
    [TYPE]: 'system',
    [DISPLAY_TYPE]: 'inline',
    [PAGE_SECTION]: 'in-store e-receipts details',
    [MESSAGE]: `we're sorry! a temporary connection error prevented your request from going through. please try again.`,
  });
};

export const setViewEReceipt = () => {
  pushEventInfo({
    [EVENT_INFO]: {
      [EVENT_NAME]: 'view e-receipt image',
    },
    [CATEGORY]: {
      [PRIMARY_CATEGORY]: 'overlay',
    },
  });
};

export const setPrintEReceipt = () => {
  pushEventInfo({
    [EVENT_INFO]: {
      [EVENT_NAME]: 'my account settings',
    },
    [CATEGORY]: {
      [PRIMARY_CATEGORY]: 'button submission',
    },
    [MY_ACCOUNT]: {
      settings: 'receipt: print image',
    },
  });
};

export const setEReceiptSort = (sort: string = '') => {
  pushEventInfo({
    [EVENT_INFO]: {
      [EVENT_NAME]: 'my account settings',
    },
    [CATEGORY]: {
      [PRIMARY_CATEGORY]: 'button submission',
    },
    [MY_ACCOUNT]: {
      settings: `purchase history: sort by ${sort.toLowerCase()}`,
    },
  });
};

export const setEReceiptFilter = (filter: string = '') => {
  pushEventInfo({
    [EVENT_INFO]: {
      [EVENT_NAME]: 'my account settings',
    },
    [CATEGORY]: {
      [PRIMARY_CATEGORY]: 'button submission',
    },
    [MY_ACCOUNT]: {
      settings: `purchase history: filter by ${filter.toLowerCase()}`,
    },
  });
};
